import { isRoutineInNote } from '../../ui/routines.js';
/**
 * Mobile "Today" list for the active doc note (execution model). Same data
 * and semantics as interfaces/classic/views/notes/today.js on the desktop; markup uses the
 * mobile action dispatcher (`data-mobile-action="today-*"`, handled in
 * interfaces/classic/mobile/actions/today-actions.js).
 */

import { routineTaskRow } from '../../ui/routine-task-row.js';
import { formatEstimate } from "../../../../engine/core/estimate.js";
import { checkIcon, pinIcon, xIcon, dragHandleIcon } from "../../../../engine/core/exec-icons.js";
import { executionGroup, groupExecutionItems, noteDay } from "../../../../engine/core/execution-list.js";
import { escapeAttr, escapeHtml } from "../utils/html.js";
import { t } from "../../../../shared/i18n/index.js";

/** Available only on the main document once its execution map is enabled. */
export function isMobileTodayAvailable(deps) {
  const sc = deps?.callbacks?.sharedCallbacks;
  const docId = deps?.getActiveDocIdForNotes?.() || null;
  const mainDocId = deps?.callbacks?.Store?.get?.("currentDocId") || null;
  return !!(sc?.isExecutionEnabled?.() && sc.getExecutionItems && (!docId || docId === mainDocId));
}

export function renderMobileToday(note, deps) {
  if (!note || !isMobileTodayAvailable(deps)) return "";
  const sc = deps.callbacks.sharedCallbacks;
  const uiState = deps.uiState || {};
  const readOnly = !!deps.callbacks.Store?.isReadOnly?.();
  const isDone = id => !!sc.nodeIsDone?.(sc.find?.(id));
  const items = groupExecutionItems(sc.getTaskListItems(note.id), isDone);
  const stats = sc.getExecutionStats?.();
  const isCurrent = !!stats?.currentPeriod && stats.currentPeriod.noteId === note.id;
  const day = noteDay(note);
  const markTitle = day ? t("routines.today.markWorkedDay", { day }) : t("routines.today.markWorked");
  const candidates = isCurrent && !readOnly && sc.getCarryCandidates ? sc.getCarryCandidates(note.id) : [];  const nid = (id) => `data-note-id="${escapeAttr(note.id)}" data-node-id="${escapeAttr(id)}"`;

  const counts = { current: 0, postponed: 0, completed: 0 };
  for (const item of items) counts[executionGroup(item, isDone)]++;
  const groupKeys = { current: "routines.today.current", postponed: "routines.today.postponed", completed: "routines.today.completed" };
  const untitled = t("common.untitled");
  const missing = t("routines.list.missingNode");
  let previousGroup = null;
  const rows = items.map((item) => {
    const node = sc.find?.(item.nodeId);
    const doneTagId = sc.getDoneTagId?.(item.nodeId);
    const name = node ? (node.name || node.text || untitled) : missing;
    const done = node ? !!sc.nodeIsDone?.(node) : false;
    const n = stats?.nodes?.[item.nodeId];
    const worked = item.workedOn.length > 0;
    const badges = [];
    if (n?.workedDays) badges.push(`<span class="mobile-today-badge">${escapeHtml(t("routines.daysShort", { n: n.workedDays }))}</span>`);
    if (node?.estimate) badges.push(`<span class="mobile-today-badge">${escapeHtml(formatEstimate(node.estimate))}</span>`);
    if (n?.overrunDays) badges.push(`<span class="mobile-today-badge warn">${escapeHtml(t("mobile.today.overrun", { n: n.overrunDays }))}</span>`);
    if (isCurrent && n?.carriedWithoutWork > 1) badges.push(`<span class="mobile-today-badge warn">↻${n.carriedWithoutWork}</span>`);
    const group = executionGroup(item, isDone);
    const heading = group !== previousGroup
      ? `<li class="execution-group-heading execution-${group}-heading">${escapeHtml(t(groupKeys[group], { n: counts[group] }))}</li>` : '';
    previousGroup = group;
    if(item.routine)return heading + routineTaskRow(item, {mobile: true, readOnly, node});
    if (item.postponedUntil && !done) badges.push(`<span class="execution-return-date">${escapeHtml(t("routines.today.returns", { date: item.postponedUntil }))}</span>`);
    return `${heading}
      <li class="mobile-today-item${done ? " done" : ""}${item.pinned ? " pinned" : ""}" data-execution-row data-execution-id="${escapeAttr(item.executionId)}" data-execution-group="${group}" data-node-id="${escapeAttr(item.nodeId)}">
        ${readOnly ? '' : `<button type="button" class="execution-drag-handle" data-execution-drag aria-label="${escapeAttr(t("routines.list.reorder", { name }))}" title="${escapeAttr(t("routines.list.reorderHint"))}">${dragHandleIcon()}</button>`}
        <button class="mobile-today-check${worked ? " on" : ""}" data-mobile-action="today-tick" ${nid(item.nodeId)}
                role="checkbox" aria-checked="${worked ? "true" : "false"}" data-worked-dates="${escapeAttr(JSON.stringify(item.workedOn))}" aria-label="${escapeAttr(t("routines.today.worked"))}" title="${escapeAttr(worked ? t("mobile.today.workedTitle") : markTitle)}" ${readOnly ? "disabled" : ""}>${checkIcon()}</button>
        <div class="mobile-today-title-cell">
          <span class="execution-title">
            <button type="button" class="mobile-today-name" data-execution-title title="${escapeAttr(t("routines.list.focusTask"))}">${escapeHtml(name)}</button>
            ${readOnly || !node || sc.isNodeReadOnly?.(item.nodeId) ? '' : `<button type="button" class="execution-rename" data-execution-rename title="${escapeAttr(t("routines.list.renameTask"))}" aria-label="${escapeAttr(t("routines.list.renameTask"))}">&#x270E;</button>`}
          </span>
          ${badges.length ? `<span class="mobile-today-badges">${badges.join("")}</span>` : ''}
          ${!readOnly && sc.canPostponeTask?.(item.nodeId) ? `<button type="button" class="mobile-task-postpone" data-task-postpone="${escapeAttr(item.nodeId)}">${escapeHtml(item.postponedUntil ? t("mobile.today.changeDate") : t("mobile.today.postpone"))}</button>` : ''}
        </div>
        ${readOnly ? "" : `
        <button class="mobile-today-btn${item.pinned ? " on" : ""}" data-mobile-action="today-pin" ${nid(item.nodeId)} aria-label="${escapeAttr(item.pinned ? t("mobile.today.unpin") : t("mobile.today.pin"))}">${pinIcon(item.pinned)}</button>
        <button class="mobile-today-btn${done ? " on" : ""}" data-mobile-action="today-done" ${nid(item.nodeId)} aria-label="${escapeAttr(t("common.done"))}" ${doneTagId ? "" : "disabled"}>${checkIcon()}</button>
        <button class="mobile-today-btn" data-mobile-action="today-remove" ${nid(item.nodeId)} aria-label="${escapeAttr(t("routines.list.takeOff"))}">${xIcon()}</button>`}
      </li>`;
  }).join("");

  const carryOpen = !!uiState.todayCarryOpen && candidates.length;
  const carryPanel = carryOpen ? `
    <div class="mobile-today-carry">
      ${candidates.map((c) => {
        const node = sc.find?.(c.nodeId);
        const name = node ? (node.name || node.text || untitled) : missing;
        return `<label class="mobile-today-carry-row">
          <input type="checkbox" class="mobile-today-carry-pick" data-node-id="${escapeAttr(c.nodeId)}" ${c.pinned ? "checked" : ""} />
          <span>${escapeHtml(name)}</span>
          ${c.carriedWithoutWork > 1 ? `<span class="mobile-today-badge warn">↻${c.carriedWithoutWork}</span>` : ""}
          ${c.pinned ? `<span class="mobile-today-badge mobile-today-badge-icon" aria-label="${escapeAttr(t("routines.today.pinnedBadge"))}">${pinIcon(true)}</span>` : ""}
        </label>`;
      }).join("")}
      <div class="mobile-today-carry-actions">
        <button class="mobile-text-btn" data-mobile-action="today-carry-confirm" data-note-id="${escapeAttr(note.id)}">${escapeHtml(t("routines.today.carrySelected"))}</button>
        <button class="mobile-text-btn" data-mobile-action="today-carry-cancel">${escapeHtml(t("common.cancel"))}</button>
      </div>
    </div>` : "";

  return `
    <section class="mobile-today" data-note-id="${escapeAttr(note.id)}" data-doc-id="${escapeAttr(deps.getActiveDocIdForNotes?.() || deps.callbacks.Store?.get?.('currentDocId') || '')}">
      <div class="mobile-today-header"><span class="mobile-today-title">${escapeHtml(t("routines.today.title"))}</span>${candidates.length ? `<button class="mobile-text-btn" data-mobile-action="today-carry-toggle">${escapeHtml(t("routines.today.carryOver", { n: candidates.length }))}</button>` : ""}</div>
      ${carryPanel}
      ${isCurrent && !readOnly ? `
      <div class="mobile-today-add">
        <input id="mobile-today-add" class="mobile-input-control" data-note-id="${escapeAttr(note.id)}" value="${escapeAttr(uiState.todayDrafts?.get(note.id) || '')}" aria-label="${escapeAttr(t("routines.today.newTask"))}" placeholder="${escapeAttr(t("routines.today.newTaskPlaceholder", { branch: t("sync.unsorted.name") }))}" enterkeyhint="done" />
        <button class="mobile-text-btn" data-mobile-action="today-add" data-note-id="${escapeAttr(note.id)}">${escapeHtml(t("common.add"))}</button>
      </div>` : ""}
      <ul class="mobile-today-list">${rows || `<li class="mobile-muted mobile-today-empty">${escapeHtml(isCurrent && !readOnly ? t("mobile.today.emptyHint", { button: t("mobile.node.list"), branch: t("sync.unsorted.name") }) : t("routines.today.empty"))}</li>`}</ul>
    </section>`;
}

/** Text for the node panel's Today button: null when unavailable. */
export function mobileTodayButtonState(nodeId, deps) {
  if (!isMobileTodayAvailable(deps)) return null;
  const sc = deps.callbacks.sharedCallbacks;
  const stats = sc.getExecutionStats?.();
  const noteId = stats?.currentPeriod?.noteId || null;
  if (!noteId) return { noteId: null, on: false };
  const on = sc.getExecutionItems({ noteId }).some((e) => e.nodeId === nodeId) || isRoutineInNote(nodeId, noteId);
  return { noteId, on };
}
